import React, { FC, useState } from 'react';
import { Box, TextField, MenuItem } from '@mui/material';
import { ScanResultExportFormats, ScanResultExportDataTypes } from '@services';
import {
    CustomFormValidators,
    FormValidationResult,
    FormValidator,
    Validators,
} from '../../helpers/validators';

interface ScanExportConfigForm {
    awsAccountId: string;
    dataFormat: ScanResultExportFormats;
    dataTypes: ScanResultExportDataTypes;
}

interface ScanExportConfigComponentProps {
    onFormChange: (data: ScanExportConfigForm, hasErrors: boolean) => void;
    formData: ScanExportConfigForm;
}

const ScanExportConfigComponent: FC<ScanExportConfigComponentProps> = ({
                                                                           onFormChange,
                                                                           formData,
                                                                       }) => {
    const formState = useState<ScanExportConfigForm>({ ...formData });
    const errorState = useState<FormValidationResult>({});
    const [form] = formState;
    const [errors] = errorState;

    const formValidators: CustomFormValidators<ScanExportConfigForm> = {
        awsAccountId: [
            (v) => Validators.required(v),
            (v) => Validators.regex(v, '^\\d{12}$', 'Must be exactly 12 digits'),
        ],
    };

    const formValidator = new FormValidator<ScanExportConfigForm>(
        formValidators,
        formState,
        errorState,
        () => onFormChange(form, Object.keys(errors).length > 0),
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }} data-testid="ScanExportConfigComponent">
            <TextField
                fullWidth
                required
                name="awsAccountId"
                label="AWS Account ID"
                value={form.awsAccountId}
                onChange={(e) => {
                    // Only allow digits and limit to 12 characters
                    const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                    formValidator.handleFormChange({ target: { name: 'awsAccountId', value } });
                }}
                error={!!errors.awsAccountId}
                helperText={errors.awsAccountId?.errorSummary || 'Your 12-digit AWS account ID'}
                placeholder="123456789012"
            />

            <TextField
                fullWidth
                select
                name="dataFormat"
                label="Data Format"
                value={form.dataFormat}
                onChange={formValidator.handleFormChange}
                helperText="Choose the format for exported data files"
            >
                <MenuItem value="Csv">CSV</MenuItem>
                <MenuItem value="Json">JSON</MenuItem>
            </TextField>

            <TextField
                fullWidth
                select
                name="dataTypes"
                label="Data Types"
                value={form.dataTypes}
                onChange={formValidator.handleFormChange}
                helperText="Select which data to export"
            >
                <MenuItem value="Measurements">Measurements Only</MenuItem>
                <MenuItem value="SizeMatch">Size Match Only</MenuItem>
                <MenuItem value="MeasurementsAndSizeMatch">Measurements and Size Match</MenuItem>
            </TextField>
        </Box>
    );
};

export default ScanExportConfigComponent;
